/* Present two independent Shopify forms as one accessible purchase choice. */
if (!customElements.get('avalon-purchase-options')) {
  customElements.define('avalon-purchase-options', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.choices = [...this.querySelectorAll('input[type="radio"]')];
      this.panels = [...this.querySelectorAll('[data-purchase-panel]')];
      this.onChange = () => this.update();
      this.onHashChange = () => {
        if (location.hash === '#one-time-archive') {
          const choice = this.choices.find(input => input.value === 'one-time');
          if (choice) {
            choice.checked = true;
            this.update();
            this.scrollIntoView({block: 'start'});
          }
        }
      };
      this.addEventListener('change', this.onChange);
      window.addEventListener('hashchange', this.onHashChange);
      this.update();
      this.onHashChange();
    }

    update() {
      const selected = this.choices.find(input => input.checked)?.value || 'membership';
      for (const panel of this.panels) {
        const inactive = panel.dataset.purchasePanel !== selected;
        panel.hidden = inactive;
        panel.disabled = inactive;
        panel.toggleAttribute('inert', inactive);
      }
      // Keep the page heading stable; the selected card and panel describe the purchase.
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      window.removeEventListener('hashchange', this.onHashChange);
      this.initialized = false;
    }
  });
}


// Country choice is confirmed against Shopify's response, never a guessed region.
if (!customElements.get('avalon-delivery-destination')) {
  customElements.define('avalon-delivery-destination', class extends HTMLElement {
    connectedCallback() {
      if (this.destinationInitialized) return;
      this.form = this.querySelector('form');
      this.select = this.querySelector('select[name="country_code"]');
      this.status = this.querySelector('[data-destination-status]');
      this.changeButton = this.querySelector('[data-change-country]');
      this.purchase = this.closest('.avalon-reference-join__copy')?.querySelector('avalon-purchase-options');
      if (!this.form || !this.select || !this.status || !this.purchase || !this.changeButton) return;
      this.destinationInitialized = true;
      this.confirmedText = this.status.textContent.trim();
      this.storageKey = 'avalon-delivery-country-v1';
      this.pendingKey = 'avalon-delivery-pending-v1';
      let remembered = '', pending = null;
      this.hasStorage = true;
      try {
        localStorage.setItem('avalon-storage-check', '1');
        localStorage.removeItem('avalon-storage-check');
        remembered = localStorage.getItem(this.storageKey);
        pending = JSON.parse(sessionStorage.getItem(this.pendingKey) || 'null');
        sessionStorage.removeItem(this.pendingKey);
      } catch (_) { this.hasStorage = false; }
      const applied = this.dataset.appliedCountry;
      const returned = pending?.country === applied && !!applied;
      if (returned) {
        remembered = applied;
        try { localStorage.setItem(this.storageKey, applied); } catch (_) {}
        const choices = [...this.purchase.querySelectorAll('input[type="radio"]')];
        const choice = choices.find(input => input.value === pending.choice);
        if (choice) {
          choices.forEach(input => { input.checked = input === choice; });
          this.purchase.update?.();
        } else if (pending.choice && pending.choice !== 'membership') {
          this.confirmedText += ' — that purchase option is not available here. Choose from the options below.';
        }
      }
      this.onDestinationChange = () => {
        if (this.select.value) this.form.requestSubmit();
      };
      this.onDestinationSubmit = event => {
        const option = this.select.selectedOptions[0];
        if (!option?.value || !option.dataset.productUrl) { event.preventDefault(); return; }
        if (option.value === applied) {
          event.preventDefault();
          try { localStorage.setItem(this.storageKey, applied); } catch (_) {}
          this.showConfirmed();
          this.status.focus();
          return;
        }
        // Use Shopify's native localization POST. It updates the country cookie
        // and returns to the matching regional product; no commerce/cart mutation.
        const url = new URL(option.dataset.productUrl, location.href);
        if (url.origin !== location.origin) { event.preventDefault(); return; }
        const choice = this.purchase.querySelector('input[type="radio"]:checked')?.value || 'membership';
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        if (choice === 'one-time') url.hash = 'one-time-archive';
        let returnTo = this.form.querySelector('[name="return_to"]');
        if (!returnTo) {
          returnTo = document.createElement('input');
          returnTo.type = 'hidden'; returnTo.name = 'return_to'; this.form.append(returnTo);
        }
        returnTo.value = url.pathname + url.search + url.hash;
        try { sessionStorage.setItem(this.pendingKey, JSON.stringify({country:option.value, choice})); } catch (_) {}
        this.purchase.hidden = true;
        this.purchase.setAttribute('inert', '');
        this.status.dataset.state = 'pending';
        this.status.textContent = `Updating Archive options for ${option.textContent.trim()}…`;
      };
      this.onChangeCountry = () => {
        this.form.hidden = false;
        this.changeButton.hidden = true;
        this.select.value = applied;
        this.select.focus();
      };
      // A cached page can contain the old country and a pending POST state.
      // Re-read Shopify's confirmed country rather than reviving those prices.
      this.onDestinationPageShow = event => {
        if (event.persisted) location.reload();
      };
      window.addEventListener('pageshow', this.onDestinationPageShow);
      this.form.addEventListener('change', this.onDestinationChange);
      this.form.addEventListener('submit', this.onDestinationSubmit);
      this.changeButton.addEventListener('click', this.onChangeCountry);
      this.querySelector('[data-destination-continue]').hidden = true;
      if (applied && (remembered === applied || !this.hasStorage)) {
        this.showConfirmed();
        if (returned) this.status.focus();
      } else {
        this.form.hidden = false;
        this.select.value = '';
        this.purchase.hidden = true;
        this.purchase.setAttribute('inert', '');
        this.changeButton.hidden = true;
        this.status.dataset.state = 'pending';
        this.status.textContent = pending
          ? 'Your destination could not be confirmed. Choose your delivery country again.'
          : 'Choose your delivery country to see available Archive options.';
      }
    }
    showConfirmed() {
      this.form.hidden = true;
      this.changeButton.hidden = false;
      this.purchase.hidden = false;
      this.purchase.removeAttribute('inert');
      this.status.dataset.state = 'applied';
      this.status.textContent = this.confirmedText;
    }
    disconnectedCallback() {
      window.removeEventListener('pageshow', this.onDestinationPageShow);
      this.form?.removeEventListener('change', this.onDestinationChange);
      this.form?.removeEventListener('submit', this.onDestinationSubmit);
      this.changeButton?.removeEventListener('click', this.onChangeCountry);
      this.destinationInitialized = false;
    }
  });
}
